import type { ReactNode } from 'react';
import { Apple, Compass, Globe, HelpCircle, Server } from 'lucide-react';

/**
 * WHERE AN ACTIVITY-LOG ROW CAME FROM — one reading shared by the list's "Device & IP" column and the
 * detail dialog's "Came in from", so the two can never disagree.
 *
 * Named the way people name them — "Android", "iOS", "Chrome", "Edge" — each with its own logo
 * (owner, 2026-09-28: "show the proper icon and write Android, iOS, Chrome").
 *
 * Sources, best first: the login session the row came through (`device`: platform, app version, phone
 * model — recorded from 2026-09-28); the row's own payload when it names a platform (a push-device
 * row carries "platform: android, deviceModel: …"); the request's user agent (browser + OS); then
 * `source`. Rows from before device capture carry only a defaulted "WEB" — which was wrong for every
 * Android action — so with nothing else to go on they say "Not recorded" rather than repeat that guess.
 */
export interface DeviceInfo {
  platform: string | null;
  appVersion: string | null;
  deviceModel: string | null;
  deviceId: string | null;
}
export interface OriginInput {
  source: string | null;
  ipAddress: string | null;
  userAgent?: string | null;
  device?: DeviceInfo | null;
  oldValue?: any;
  newValue?: any;
}
export interface ClientOrigin {
  kind: 'ANDROID' | 'IOS' | 'WEB' | 'SYSTEM' | 'API' | 'UNKNOWN';
  icon: ReactNode;
  /** "Android", "iOS", "Chrome", "Edge", "System" … */
  label: string;
  /** "App 1.3.0" for the apps, the browser's major version ("128") for a browser. */
  version: string | null;
  /** The phone model for an app; the operating system for a browser. */
  detail: string | null;
}

/* ── Logos. Drawn here (the app ships no brand-icon set): recognisable, not pixel copies. ── */
function AndroidLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path d="M3.5 18a8.5 8.5 0 0 1 17 0Z" fill="#3DDC84" />
      <path d="M7.4 10.6 5.6 7.6M16.6 10.6l1.8-3" stroke="#3DDC84" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="8.6" cy="14.4" r="1" fill="#fff" />
      <circle cx="15.4" cy="14.4" r="1" fill="#fff" />
    </svg>
  );
}
function ChromeLogo({ className }: { className?: string }) {
  // Three 120° sectors (red top, yellow right, green left), a white ring, a blue centre.
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path d="M12 12 3.34 7A10 10 0 0 1 20.66 7Z" fill="#EA4335" />
      <path d="M12 12 20.66 7A10 10 0 0 1 12 22Z" fill="#FBBC04" />
      <path d="M12 12 12 22A10 10 0 0 1 3.34 7Z" fill="#34A853" />
      <circle cx="12" cy="12" r="4.8" fill="#fff" />
      <circle cx="12" cy="12" r="3.7" fill="#4285F4" />
    </svg>
  );
}
const ICON = 'size-4 shrink-0';
const BROWSER_ICON: Record<string, ReactNode> = {
  Chrome: <ChromeLogo className={ICON} />,
  Edge: <Globe className={ICON} style={{ color: '#0C8CE9' }} />,
  Firefox: <Globe className={ICON} style={{ color: '#FF7139' }} />,
  Safari: <Compass className={ICON} style={{ color: '#006CFF' }} />,
  Opera: <Globe className={ICON} style={{ color: '#FF1B2D' }} />,
  Browser: <Globe className={ICON} />,
};

/** Browser name, major version and OS from a user agent; null when it is not a browser. */
export function browserOf(ua: string | null | undefined): { name: string; version: string | null; os: string | null } | null {
  if (!ua || !/mozilla/i.test(ua)) return null;
  const major = (re: RegExp) => ua.match(re)?.[1]?.split('.')[0] ?? null;
  const [name, version] = /edg\//i.test(ua) ? ['Edge', major(/edg\/([\d.]+)/i)]
    : /opr\//i.test(ua) ? ['Opera', major(/opr\/([\d.]+)/i)]
      : /firefox\//i.test(ua) ? ['Firefox', major(/firefox\/([\d.]+)/i)]
        : /chrome\//i.test(ua) ? ['Chrome', major(/chrome\/([\d.]+)/i)]
          : /safari\//i.test(ua) ? ['Safari', major(/version\/([\d.]+)/i)]
            : ['Browser', null];
  const os = /windows/i.test(ua) ? 'Windows'
    : /android/i.test(ua) ? 'Android'
      : /iphone|ipad|ipod/i.test(ua) ? 'iOS'
        : /mac os x|macintosh/i.test(ua) ? 'macOS'
          : /linux/i.test(ua) ? 'Linux' : null;
  return { name, version, os };
}

/** A platform/model named inside the row's own payload (push-device rows carry both). */
function payloadDevice(log: OriginInput): { platform: string; model: string | null } | null {
  for (const v of [log.newValue, log.oldValue]) {
    if (v && typeof v === 'object' && !Array.isArray(v) && typeof v.platform === 'string') {
      return { platform: v.platform.toUpperCase(), model: typeof v.deviceModel === 'string' ? v.deviceModel : null };
    }
  }
  return null;
}

export function describeClient(log: OriginInput): ClientOrigin {
  const src = (log.source ?? '').toUpperCase();
  const ua = log.userAgent ?? '';
  const fromPayload = log.device ? null : payloadDevice(log);
  const platform = (log.device?.platform ?? fromPayload?.platform ?? '').toUpperCase();
  const appVersion = log.device?.appVersion ?? null;
  const model = log.device?.deviceModel ?? fromPayload?.model ?? null;

  if (platform === 'ANDROID' || /ANDROID/.test(src) || /okhttp|dalvik/i.test(ua)) {
    return { kind: 'ANDROID', icon: <AndroidLogo className={ICON} />, label: 'Android', version: appVersion ? `App ${appVersion}` : null, detail: model };
  }
  if (platform === 'IOS' || src === 'IOS' || /cfnetwork|darwin/i.test(ua)) {
    return { kind: 'IOS', icon: <Apple className={ICON} />, label: 'iOS', version: appVersion ? `App ${appVersion}` : null, detail: model };
  }
  const b = browserOf(ua);
  if (b || platform === 'WEB') {
    const name = b?.name ?? 'Browser';
    // No version chip for a browser: "Chrome 153" tells an admin nothing about who did what (unlike the app
    // build on a phone, which says whether they are on the current release).
    return { kind: 'WEB', icon: BROWSER_ICON[name] ?? BROWSER_ICON.Browser, label: name, version: null, detail: b?.os ?? null };
  }
  if (src === 'SYSTEM' || src.startsWith('CRON') || src === 'AUTO' || src === 'IMPORT') {
    return { kind: 'SYSTEM', icon: <Server className={ICON} />, label: 'System', version: null, detail: src === 'IMPORT' ? 'bulk import' : 'scheduled job' };
  }
  if (src === 'API') {
    return { kind: 'API', icon: <Globe className={ICON} />, label: 'API client', version: null, detail: ua || null };
  }
  // Only the old default ("WEB") or nothing at all: the row predates device capture.
  return { kind: 'UNKNOWN', icon: <HelpCircle className={ICON} />, label: 'Not recorded', version: null, detail: 'logged before device details were kept' };
}

/**
 * The IP in plain words. `address` is the bare address (IPv4-mapped "::ffff:" prefix stripped),
 * `where` says what kind of address it is, and `text` is the one-line form the list uses.
 */
export function describeIp(ip: string | null | undefined): { text: string; address: string; where: string; title?: string } | null {
  if (!ip) return null;
  const v = ip.replace(/^::ffff:/i, '');
  if (v === '::1' || v === '127.0.0.1' || v === 'localhost') return { text: 'This computer (local)', address: v, where: 'This computer', title: ip };
  if (/^10\./.test(v) || /^192\.168\./.test(v) || /^172\.(1[6-9]|2\d|3[01])\./.test(v)) return { text: `Local network (${v})`, address: v, where: 'Local network', title: ip };
  return { text: v, address: v, where: 'Internet', title: ip };
}

/** The list's "Device & IP" cell: logo + name + version, the phone model or OS, then the IP. */
export function ClientCell({ log }: { log: OriginInput }) {
  const c = describeClient(log);
  const ip = describeIp(log.ipAddress);
  return (
    <div className="min-w-0 leading-tight">
      <div className={`flex items-center gap-1.5 text-xs ${c.kind === 'UNKNOWN' ? 'italic text-muted' : 'font-medium text-content'}`}>
        {c.icon}
        <span className="truncate">{c.label}</span>
        {c.version && <span className="shrink-0 rounded bg-surface-2 px-1 font-mono text-2xs font-normal text-muted">{c.version}</span>}
      </div>
      {c.detail && c.kind !== 'UNKNOWN' && <div className="mt-0.5 truncate pl-5.5 text-2xs text-muted" title={c.detail}>{c.detail}</div>}
      {ip && <div className="mt-0.5 truncate pl-5.5 font-mono text-2xs text-subtle" title={ip.title}>{ip.text}</div>}
    </div>
  );
}
